import type { FC } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';

import { ErrorPageComponent } from '@confluence/not-found';
import { HOME } from '@confluence/named-routes';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';

import RestrictedContentIconLight from './RestrictedContentIcon-Light.svg';
import RestrictedContentIconDark from './RestrictedContentIcon-Dark.svg';

const i18n = defineMessages({
	errorTitle: {
		id: 'forge-ui.components.restricted-page.errorTitle',
		defaultMessage: 'An admin has blocked this app ',
		description: 'Title for section that appears when the app is blocked',
	},
	errorBodyWithTitle: {
		id: 'forge-ui.components.restricted-page.errorBodyWithTitle',
		defaultMessage:
			'<b>{appName}</b> has been blocked by your administrator in this space through a data security policy.{br}{br} For any questions, reach out to your organization admin.',
		description: 'Body for section that appears when the app is blocked and app name is present',
	},
	errorBodyWithoutTitle: {
		id: 'forge-ui.components.restricted-page.errorBodyWithoutTitle',
		defaultMessage:
			'<b>This app</b> has been blocked by your administrator in this space through a data security policy.{br}{br} For any questions, reach out to your organization admin.',
		description:
			'Body for section that appears when the app is blocked and app name is not present',
	},
	goHome: {
		id: 'forge-ui.components.restricted-page.goHomeLink',
		defaultMessage: 'Go home',
		description: 'A link to the home page',
	},
});

type ForgeRestrictedPageProps = {
	appName?: string;
};

export const ForgeRestrictedPage: FC<ForgeRestrictedPageProps> = ({ appName }) => {
	const intl = useIntl();
	const { push } = useRouteActions();

	const errorMessageSharedArguments = {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
		b: (...chunks) => <span style={{ fontWeight: token('font.weight.semibold') }}>{chunks}</span>,
		br: <br />,
	};
	const formattedErrorMessage = appName
		? intl.formatMessage(i18n.errorBodyWithTitle, {
				appName,
				...errorMessageSharedArguments,
			})
		: intl.formatMessage(i18n.errorBodyWithoutTitle, errorMessageSharedArguments);

	return (
		<ErrorPageComponent
			errorImage={RestrictedContentIconLight}
			errorImageDark={RestrictedContentIconDark}
			errorTitle={intl.formatMessage(i18n.errorTitle)}
			errorMessage={formattedErrorMessage}
			primaryButtonProps={{
				children: intl.formatMessage(i18n.goHome),
				onClick: () => push(HOME.toUrl()),
			}}
		/>
	);
};
